/**
 * Die Konfiguration eines Agentenschrittes — Voreinstellungen, Umrechnung, Prüfung.
 *
 * Reine Logik, getrennt von der Oberfläche: Was hier falsch ist, sieht man einem Formular nicht an.
 * Drei Dinge gehen leicht schief und sind deshalb hier und nicht im Panel:
 *
 * 1. **Die Einheit.** Zeitlimit und Werkzeug-Timeout werden in **Sekunden** eingegeben und in
 *    **Millisekunden** gespeichert. Ein Faktor 1000 an der falschen Stelle ist ein Fehler, den man
 *    erst merkt, wenn ein Agent nach 300 Millisekunden aufgibt oder fünf Tage läuft.
 * 2. **Die Erlaubnis.** Ein nicht ausgewähltes Werkzeug hat *keinen* Eintrag in `policy` —
 *    „verboten" ist die Abwesenheit. Ein dritter Zustand liesse zu, dass ein Werkzeug gleichzeitig
 *    ausgewählt und verboten ist.
 * 3. **Die Freigabe.** Ob eine Rolle verlangt wird, hängt vom Zustand zweier anderer Abschnitte ab.
 *    Diese Bedingung an zwei Stellen zu wiederholen hiesse, sie irgendwann an einer zu ändern.
 */

import type {
  AgentStepConfig,
  ExitBehaviour,
  RunStatusGroup,
  ToolPolicy,
} from "@the-real-insight/tri-model";

export type { AgentStepConfig, ExitBehaviour, RunStatusGroup, ToolPolicy };

/** Ein Werkzeug, wie es der Server aus dem Register liefert. */
export interface CatalogueTool {
  name: string;
  label: string;
  description: string;
  category: string;
  sideEffects: boolean;
}

/** Die Fassung, nach der die Felder eines Schrittes zu lesen sind. */
export const STEP_SCHEMA_VERSION = 2;

/**
 * Die Ausgänge eines Agentenschrittes.
 *
 * **Das Ziel steht nicht dabei, und das ist der Punkt.** Ein Prompt-Tool wird von mehreren
 * Workflows benutzt; wohin es nach einem Ausgang weitergeht, ist eine Eigenschaft des Prozesses.
 * Das Werkzeug entscheidet, **wie** ein Lauf ausgeht — der Prozess verzweigt im BPMN über den
 * gemeldeten Status.
 *
 * Drei der sechs Ausgänge sind in ihrer Meldung festgelegt: „Erfolgreich" ist ein Erfolg,
 * „Fehler" ein Fehler, und eine Rückfrage an einen Menschen ist eine Rückfrage. Wählbar ist nur,
 * was wirklich zwei Lesarten hat.
 *
 * `failed` steht für `failed` **und** `aborted`: Für den Prozess sind beide derselbe Ausgang.
 */
export const ROUTING_ROWS: Array<{
  status: RunStatusGroup;
  /** Die Voreinstellung der Meldung. */
  behaviour: ExitBehaviour;
  /** Ob sich die Meldung ändern lässt, oder ob sie aus der Natur des Ausgangs folgt. */
  fixed?: boolean;
  /** Ob die Zeile eine Wiederholungszahl trägt, und wie viele es voreingestellt sind. */
  retries?: number;
}> = [
  { status: "completed", behaviour: "success", fixed: true },
  { status: "partial", behaviour: "success" },
  { status: "budget_exceeded", behaviour: "error", retries: 0 },
  { status: "schema_violation", behaviour: "error", retries: 1 },
  { status: "waiting_human", behaviour: "ask", fixed: true },
  { status: "failed", behaviour: "error", fixed: true, retries: 1 },
];

/** Die Meldung eines Ausgangs — die feste, wo sie feststeht, sonst die eingestellte. */
export const behaviourOf = (config: AgentStepConfig, status: RunStatusGroup): ExitBehaviour => {
  const zeile = ROUTING_ROWS.find(row => row.status === status);
  if (!zeile) return "error";
  if (zeile.fixed) return zeile.behaviour;
  return config.routing?.[status]?.behaviour ?? zeile.behaviour;
};

export const BUDGET_LIMITS = {
  maxTurns: { min: 1, max: 200, default: 20 },
  maxTotalTokens: { min: 1000, max: 5_000_000, default: 200_000 },
  /** In Sekunden — so wird es eingegeben. Gespeichert wird `wallClockMs`. */
  wallClockSeconds: { min: 10, max: 3600, default: 300 },
  /** Ebenfalls in Sekunden; gespeichert als `toolCallTimeoutMs`. */
  toolCallTimeoutSeconds: { min: 1, max: 600, default: 60 },
} as const;

/**
 * **Wie lang ein Ziel sein darf — und warum die Zahl größer ist als sie war.**
 *
 * 4.000 Zeichen waren zu knapp gegriffen: Ein Agentenziel ist in der Praxis ein Arbeitsauftrag mit
 * Rolle, Vorgehen, Datenquellen und Ausgabeformat. Gemessen am 20.09.2026 stand ein solches Ziel
 * bei **exakt 4.000** Zeichen und endete mitten im Wort — das Textfeld trug ein `maxLength` und
 * hatte beim Einfügen still gekürzt. Der Nutzer merkte es daran, dass sich nichts mehr tippen
 * liess; der verlorene Rest war nicht wiederherstellbar.
 *
 * Deshalb zweierlei: die Grenze auf 20.000 (rund 5.000 Token, das trägt jedes Modell mühelos), und
 * **kein `maxLength` mehr am Feld**. Wer darüber liegt, sieht die Zahl rot und bekommt beim
 * Speichern eine Ansage — aber nichts verschwindet mehr, ohne dass es jemand sagt.
 */
export const GOAL_MAX_LENGTH = 20000;

/** Millisekunden ↔ Sekunden. Einmal geschrieben, damit der Faktor nur an einer Stelle steht. */
export const msToSeconds = (ms: number | undefined): number => Math.round(Number(ms ?? 0) / 1000);
export const secondsToMs = (seconds: number | undefined): number =>
  Math.round(Number(seconds ?? 0) * 1000);

/** Ein leerer Agentenschritt, wie er beim ersten Umschalten entsteht. */
export const emptyAgentConfig = (): AgentStepConfig => ({
  goal: "",
  tools: { allow: [], policy: {} },
  budget: {
    maxTurns: BUDGET_LIMITS.maxTurns.default,
    maxTotalTokens: BUDGET_LIMITS.maxTotalTokens.default,
    wallClockMs: secondsToMs(BUDGET_LIMITS.wallClockSeconds.default),
    toolCallTimeoutMs: secondsToMs(BUDGET_LIMITS.toolCallTimeoutSeconds.default),
  },
  output: { allowPartial: true, requireProvenance: true },
  routing: Object.fromEntries(
    ROUTING_ROWS.map(row => [
      row.status,
      {
        behaviour: row.behaviour,
        ...(row.retries !== undefined ? { retries: row.retries } : {}),
      },
    ]),
  ) as AgentStepConfig["routing"],
});

/**
 * Eine gespeicherte Konfiguration auf eine vollständige bringen.
 *
 * Ein Datensatz aus einer früheren Fassung kennt vielleicht ein Feld nicht, und ein von Hand
 * geschriebener kennt es falsch. Beides darf das Formular nicht zum Absturz bringen: Was fehlt,
 * bekommt die Voreinstellung, was da ist, bleibt.
 */
export const withDefaults = (stored: any): AgentStepConfig => {
  const leer = emptyAgentConfig();
  if (!stored || typeof stored !== "object") return leer;

  const allow = Array.isArray(stored.tools?.allow)
    ? stored.tools.allow.filter((name: any) => typeof name === "string" && name)
    : [];
  const policy: Record<string, ToolPolicy> = {};
  for (const name of allow) {
    policy[name] = stored.tools?.policy?.[name] === "approve" ? "approve" : "auto";
  }

  return {
    goal: typeof stored.goal === "string" ? stored.goal : "",
    tools: { allow, policy },
    budget: { ...leer.budget, ...(stored.budget ?? {}) },
    output: { ...leer.output, ...(stored.output ?? {}) },
    routing: { ...leer.routing, ...(stored.routing ?? {}) },
    ...(stored.hitlRole ? { hitlRole: String(stored.hitlRole) } : {}),
  };
};

const applyToggle = (
  allow: Set<string>,
  policy: Record<string, ToolPolicy>,
  tool: CatalogueTool,
  selected: boolean,
): void => {
  if (selected) {
    allow.add(tool.name);
    if (!policy[tool.name]) policy[tool.name] = tool.sideEffects ? "approve" : "auto";
  } else {
    allow.delete(tool.name);
    delete policy[tool.name];
  }
};

/**
 * Ein Werkzeug aus- oder abwählen.
 *
 * Beim Auswählen entscheidet `sideEffects` die Voreinstellung: Was etwas **tut**, kommt auf
 * „Freigabe", was nur liest, läuft ungefragt. Beim Abwählen verschwindet der Eintrag aus `policy` —
 * sonst bliebe eine Erlaubnis für ein Werkzeug stehen, das gar nicht mehr ausgewählt ist.
 */
export const toggleTool = (
  config: AgentStepConfig,
  tool: CatalogueTool,
  selected: boolean,
): AgentStepConfig => {
  const allow = new Set(config.tools.allow);
  const policy = { ...config.tools.policy };
  applyToggle(allow, policy, tool, selected);

  return { ...config, tools: { allow: Array.from(allow), policy } };
};

/**
 * **Einen ganzen Werkzeugsatz auf einmal aus- oder abwählen.**
 *
 * Das Register liefert 301 Werkzeuge in Kästen; wer einem Agenten „alles aus tri-assets" geben
 * will, hat bis hierher 40 Kästchen einzeln angeklickt. Die Regel je Werkzeug bleibt dieselbe —
 * `toggleTool` ist nur der Fall einer Gruppe aus einem Werkzeug —, sie wird nur über die Gruppe
 * gefahren. So kann eine Sammelauswahl nichts anderes bewirken als dieselbe Zahl Einzelklicks.
 */
export const toggleToolGroup = (
  config: AgentStepConfig,
  gruppe: readonly CatalogueTool[],
  selected: boolean,
): AgentStepConfig => {
  if (!gruppe?.length) return config;

  const allow = new Set(config.tools.allow);
  const policy = { ...config.tools.policy };
  for (const tool of gruppe) applyToggle(allow, policy, tool, selected);

  return { ...config, tools: { allow: Array.from(allow), policy } };
};

/**
 * **Die Erlaubnis für einen ganzen Werkzeugsatz setzen.**
 *
 * Betroffen sind nur die **ausgewählten** Werkzeuge der Gruppe: Eine Erlaubnis für ein nicht
 * ausgewähltes Werkzeug gibt es nicht — „verboten" ist die Abwesenheit des Eintrags, und ein
 * Eintrag ohne Auswahl wäre genau der dritte Zustand, den diese Datenstruktur vermeidet.
 */
export const setzeGruppenPolitik = (
  config: AgentStepConfig,
  gruppe: readonly CatalogueTool[],
  politik: ToolPolicy,
): AgentStepConfig => {
  const policy = { ...config.tools.policy };
  for (const tool of gruppe ?? []) {
    if (config.tools.allow.includes(tool.name)) policy[tool.name] = politik;
  }
  return { ...config, tools: { ...config.tools, policy } };
};

/**
 * Welche Erlaubnis in einer Gruppe gilt — für den Schalter in ihrer Kopfzeile.
 *
 * `null` heisst „nichts ausgewählt", also nichts zu schalten; `gemischt` heisst, dass beide
 * Erlaubnisse vorkommen. Ein Schalter, der in diesem Fall eine der beiden Seiten hervorhöbe, würde
 * über die andere Hälfte der Gruppe lügen.
 */
export const gruppenPolitik = (
  config: AgentStepConfig,
  gruppe: readonly CatalogueTool[],
): ToolPolicy | "gemischt" | null => {
  const gewaehlt = (gruppe ?? []).filter(tool => config.tools.allow.includes(tool.name));
  if (!gewaehlt.length) return null;
  // Ohne `Set`-Iteration: Das Übersetzungsziel des Portals kennt sie nicht (`downlevelIteration`).
  const werte = gewaehlt.map(tool => config.tools.policy[tool.name] ?? "auto");
  return werte.every(wert => wert === werte[0]) ? (werte[0] as ToolPolicy) : "gemischt";
};

/** Wie weit eine Gruppe ausgewählt ist — für das Kästchen in ihrer Kopfzeile. */
export const gruppenAuswahl = (
  config: AgentStepConfig,
  gruppe: readonly CatalogueTool[],
): "keine" | "teilweise" | "alle" => {
  const eintraege = gruppe ?? [];
  if (!eintraege.length) return "keine";
  const gewaehlt = eintraege.filter(tool => config.tools.allow.includes(tool.name)).length;
  if (gewaehlt === 0) return "keine";
  return gewaehlt === eintraege.length ? "alle" : "teilweise";
};

/**
 * Ob die Freigaberolle verlangt wird — an einer Stelle festgelegt, an mehreren gelesen.
 *
 * Es entscheidet allein, ob ein Werkzeug Freigabe braucht. Die frühere zweite Bedingung — „die
 * Rückfrage an einen Menschen ist verdrahtet" — ist mit dem Zielfeld entfallen, und sie liesse sich
 * auch nicht ersetzen: Den Ausgang `waiting_human` hat **jeder** Agentenschritt, er tritt nur ein,
 * wenn der Agent von sich aus fragt. Daran die Pflicht zu knüpfen hiesse, sie immer zu verlangen.
 */
export const requiresHitlRole = (config: AgentStepConfig): boolean =>
  Object.values(config.tools.policy).some(policy => policy === "approve");

const ausserhalb = (wert: number, grenzen: { min: number; max: number }): boolean =>
  !Number.isFinite(wert) || wert < grenzen.min || wert > grenzen.max;

/** Welche Budgetfelder ausserhalb ihrer Grenzen liegen — je Feld, damit es am Feld steht. */
export const budgetErrors = (config: AgentStepConfig): Record<string, boolean> => ({
  maxTurns: ausserhalb(config.budget.maxTurns, BUDGET_LIMITS.maxTurns),
  maxTotalTokens: ausserhalb(config.budget.maxTotalTokens, BUDGET_LIMITS.maxTotalTokens),
  wallClockMs: ausserhalb(msToSeconds(config.budget.wallClockMs), BUDGET_LIMITS.wallClockSeconds),
  toolCallTimeoutMs: ausserhalb(
    msToSeconds(config.budget.toolCallTimeoutMs),
    BUDGET_LIMITS.toolCallTimeoutSeconds,
  ),
});

/**
 * **Welcher Text zu welchem Hinderungsgrund gehört.**
 *
 * Die Meldung beim Speichern nannte bis zum 18.09.2026 ALLE denkbaren Gründe in einem Satz —
 * „Ziel erforderlich, Budget muss gültig sein, und Freigaberolle wird verlangt …" — und damit
 * keinen. Wer sie las, wusste nicht, was an seinem Schritt fehlt. Hier steht je Grund der Text,
 * der ihn benennt; die Zuordnung liegt bei der Prüfung und nicht in der Oberfläche, damit ein
 * neuer Grund nicht stillschweigend ohne Text bleibt.
 */
export const BLOCKING_TEXTE: Record<string, string> = {
  goalEmpty: "ui.agentStep.goalRequired",
  goalTooLong: "ui.agentStep.goalTooLong",
  budgetOutOfRange: "ui.agentStep.budgetInvalid",
  // Eigener Satz statt des Feldhinweises: Die Meldung steht für sich, ohne das Feld daneben.
  hitlRoleMissing: "ui.agentStep.hitlRoleBlocking",
};

export interface AgentValidation {
  /** Was das Speichern verhindert. */
  blocking: string[];
  /** Was gespeichert werden darf, aber am Schritt vermerkt wird. */
  warnings: string[];
}

/**
 * Was das Speichern verhindert und was nur angemerkt wird.
 *
 * Die Trennung ist der Punkt: Ein Agent ohne Werkzeuge ist kein Fehler, sondern eine Entscheidung —
 * er entspricht dann „Die KI fragen". Ein nicht verdrahteter Ausgang ebenso; er fällt in die
 * allgemeine Fehlerbehandlung des Prozesses. Beides zu blockieren hiesse, unfertige Arbeit nicht
 * speichern zu können.
 */
export const validateAgentConfig = (config: AgentStepConfig): AgentValidation => {
  const blocking: string[] = [];
  const warnings: string[] = [];

  /*
   * **Die fehlende Freigaberolle sperrt — Entscheidung vom 18.09.2026.**
   *
   * Zur Laufzeit wäre sie verzichtbar: Ohne Rolle lehnt der Agentenschritt einen
   * freigabepflichtigen Aufruf ab und läuft weiter (`agentStepRuntime`: mit Rolle aufschieben,
   * ohne ablehnen). Eine Warnung hätte also gereicht, und ein Zwischenstand liesse sich speichern.
   *
   * Dagegen steht, was ein gespeicherter Schritt verspricht: Er wird unbeaufsichtigt gefahren, oft
   * nachts, und wer ein Werkzeug auf „Freigabe" stellt, hat gesagt, dass jemand zusehen soll. Ein
   * Schritt, der diesen Jemand nicht kennt, führt die Freigabe nur im Namen — er verliert das
   * Werkzeug still im Lauf, statt beim Einrichten zu fragen. Der frühe Widerspruch ist der
   * billigere.
   */
  if (!config.goal?.trim()) blocking.push("goalEmpty");
  if (config.goal && config.goal.length > GOAL_MAX_LENGTH) blocking.push("goalTooLong");
  if (Object.values(budgetErrors(config)).some(Boolean)) blocking.push("budgetOutOfRange");
  if (requiresHitlRole(config) && !config.hitlRole) blocking.push("hitlRoleMissing");

  if (!config.tools.allow.length) warnings.push("noTools");

  // Die frühere Warnung „Ausgang nicht verdrahtet" ist entfallen: Es gibt am Werkzeug kein Ziel
  // mehr, das man vergessen könnte. Verdrahtet wird im Prozess, und dort fehlt sie nicht.

  return { blocking, warnings };
};
